import { baseApi } from './baseApi';
import {
  parseNotificationsListResponse,
  parseNotificationCountsResponse,
  parseMarkReadResponse,
  parseNotificationsErrorResponse, // ✅ Added missing import
} from '../parsers/notifications.parsers';

/**
 * Notifications API service
 * Extends the base API with notification endpoints
 * 
 * @description
 * Provides endpoints for fetching notifications, marking them as read, and getting counts
 */
export const notificationsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Get notifications list
     * GET /api/notification/list?page=1&limit=10
     * 
     * @purpose Retrieve paginated list of notifications
     * @useCase Display notifications in general/important tabs
     * 
     * @request {Object} params
     * @request {number} params.page - Page number (default: 1)
     * @request {number} params.limit - Items per page (default: 10)
     * 
     * @response {Object} Paginated notifications list
     */
    getNotificationsList: builder.query({
      query: (params = {}) => ({
        url: '/api/notification/list',
        method: 'GET',
        params: {
          page: params.page || 1,
          limit: params.limit || 10,
          category: params.category || undefined,
        },
      }),
      transformResponse: (response) => {
        try {
          // Validate response exists
          if (!response) {
            console.warn('[Notifications API] Empty response received');
            return parseNotificationsListResponse(null);
          }

          // Log response in development for debugging
          if (import.meta.env.DEV) {
            console.log('[Notifications API] Raw response:', response);
          }

          const parsed = parseNotificationsListResponse(response);

          // Log parsing result in development
          if (import.meta.env.DEV && !parsed.success) {
            console.warn('[Notifications API] Parse warning:', parsed.message);
          }

          return parsed;
        } catch (error) {
          console.error('[Notifications API] Transform error:', error, response);
          return parseNotificationsListResponse(null);
        }
      },
      transformErrorResponse: (error) => {
        console.error('[Notifications API] Request error:', error);
        return parseNotificationsErrorResponse(error);
      },
      providesTags: ['Notifications'],
      refetchOnMountOrArgChange: true,
      keepUnusedDataFor: 60,
    }),

    /**
     * Get notification counts
     * GET /api/notification/counts
     * 
     * @purpose Retrieve notification counts (total, unread, general, important)
     * @useCase Display badge counts in header/tabs
     * 
     * @response {Object} Notification counts
     */
    getNotificationCounts: builder.query({
      query: () => ({
        url: '/api/notification/counts',
        method: 'GET',
      }),
      transformResponse: (response) => {
        try {
          // Validate response exists
          if (!response) {
            console.warn('[Notifications API] Empty counts response received');
            return parseNotificationCountsResponse(null);
          }

          // Log response in development for debugging
          if (import.meta.env.DEV) {
            console.log('[Notifications API] Raw counts response:', response);
          }

          const parsed = parseNotificationCountsResponse(response);

          // Log parsing result in development
          if (import.meta.env.DEV && !parsed.success) {
            console.warn('[Notifications API] Counts parse warning:', parsed.message);
          }

          return parsed;
        } catch (error) {
          console.error('[Notifications API] Counts transform error:', error, response);
          return parseNotificationCountsResponse(null);
        }
      },
      transformErrorResponse: (error) => {
        console.error('[Notifications API] Counts request error:', error);
        return parseNotificationsErrorResponse(error);
      },
      providesTags: ['Notifications'],
      refetchOnMountOrArgChange: true,
      keepUnusedDataFor: 60,
    }),

    /**
     * Mark notifications as read
     * PATCH /api/notification/mark-read
     * 
     * @purpose Mark one or more notifications as read
     * @useCase User views notifications, mark them as read
     * 
     * @request {Object} payload
     * @request {Array<number>} payload.notificationIds - Array of notification IDs to mark as read
     * 
     * @response {Object} Success response
     */
    markNotificationsRead: builder.mutation({
      query: (payload) => ({
        url: '/api/notification/mark-read',
        method: 'PATCH',
        body: payload,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response) => parseMarkReadResponse(response),
      transformErrorResponse: (error) => parseNotificationsErrorResponse(error),
      invalidatesTags: ['Notifications'],
    }),
  }),
});

// Export hooks for usage in components
export const {
  useGetNotificationsListQuery,
  useGetNotificationCountsQuery,
  useMarkNotificationsReadMutation,
} = notificationsApi;
